/** Copyright © 2024 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/

import type { LocationQueryValue } from 'vue-router';

import isNil from 'lodash-es/isNil';
import type { CellValue, Row } from 'tui-grid';

import type { ActivityHistorySortColumn } from '@hems/util/src/constant/activityHistory';
import { ACTIVITY_HISTORY_SORT_COLUMN } from '@hems/util/src/constant/activityHistory';
import type { ACSystemFieldTypeCodeValue, AppModeValue } from '@hems/util/src/constant/constant';
import { FORM_TYPE, APP_MODE, AC_SYSTEM_FIELD_TYPE_CODE } from '@hems/util/src/constant/constant';
import { ENERGY_CONTROL } from '@hems/util/src/constant/dashboard';
import type { EnergyControlValue } from '@hems/util/src/constant/dashboard';
import { GEN_TYPE } from '@hems/util/src/constant/device';
import type { ErrorLevelValue } from '@hems/util/src/constant/eventHistory';
import { ERROR_LEVEL } from '@hems/util/src/constant/eventHistory';
import type {
  FirmwareUpdateStatus,
  FirmwareUpdateType,
  SiteConfigurationType,
} from '@hems/util/src/constant/firmwareUpdate';
import {
  FIRMWARE_UPDATE_STATUS,
  FIRMWARE_UPDATE_TYPE,
  SITE_CONFIGURATION_TYPE,
} from '@hems/util/src/constant/firmwareUpdate';
import type { GraphGroupCodeValue } from '@hems/util/src/constant/graph';
import { GRAPH_GROUP_CODE } from '@hems/util/src/constant/graph';
import type { ApplianceTypeValue } from '@hems/util/src/constant/homeAppliance';
import { APPLIANCE_TYPE } from '@hems/util/src/constant/homeAppliance';
import type { LanguageValue } from '@hems/util/src/constant/languages';
import { LANGUAGE } from '@hems/util/src/constant/languages';
import type { SiteStatusCodeType } from '@hems/util/src/constant/remoteMonitoring';
import { FLEET_ROLE } from '@hems/util/src/constant/role';
import type { FleetRoleValue } from '@hems/util/src/constant/role';
import { SETTING_TARGET, OVER_09_EMS_VERSION_GRID_CODE, ENERGY_POLICY } from '@hems/util/src/constant/settings';
import type { EnergyPolicyValue, Over09EMSVersionGridCode, SettingTargetValue } from '@hems/util/src/constant/settings';
import type { BasePhysicalUnitType, UnitValueType } from '@hems/util/src/constant/unit';
import { UNIT } from '@hems/util/src/constant/unit';
import { isEmptyString } from '@hems/util/src/helper/helper';

import type { Auth, FormMode, Period } from 'hems';

import type { GridEventType, GroupSelectOption, SelectorOption } from 'hems/common/types';
import type { MonitoringListDeviceInfo, MonitoringListSiteInfo } from 'hems/device';
import type { DeviceProfileACSystem } from 'hems/device/dashboard/smartmodule/acsys';
import type { Device, DeviceAC, DeviceProfileAllGenType } from 'hems/device/siteinfo';
import type { ESSNumberType } from 'hems/device/statistics/common';
import type { ErrorCodeDisplayDetails, DisplayedSiteErrorInfo } from 'hems/event';
import type { FirmwareEMSFileNameKey, FirmwareFileNameKey, FirmwareFileVersionKey } from 'hems/firmwareUpdate';

type RouteValueType = LocationQueryValue | LocationQueryValue[] | string | string[] | number;

export const isStringType = (value: RouteValueType): value is string => {
  return typeof value === 'string';
};

export const isFormModeType = (value: RouteValueType): value is FormMode => {
  return value === FORM_TYPE.READ || value === FORM_TYPE.EDIT || value === FORM_TYPE.NEW;
};

export const getRouteInfo = <T extends RouteValueType>(
  value: RouteValueType,
  isTypeGuardValue: (value: RouteValueType) => value is T
): T | undefined => {
  if (isTypeGuardValue(value)) return value;

  return;
};

export const isPeriod = (date: Date | Period): date is Period => {
  return 'start' in date && 'end' in date;
};

export const isNumberType = (value: RouteValueType): value is number => {
  return typeof value === 'number';
};

export const getStringValue = (value?: string | null) => {
  if (value === undefined || value === null || isEmptyString(value.trim())) {
    return null;
  }

  return value;
};

export const isDeviceACType = (device: Device): device is DeviceAC => {
  return device.genType === GEN_TYPE.AC_SYS;
};

export const isACSystemType = (deviceInfo: DeviceProfileAllGenType): deviceInfo is DeviceProfileACSystem => {
  return deviceInfo.gen_type === GEN_TYPE.AC_SYS;
};

export const isGraphGroupCodeType = (value: string): value is GraphGroupCodeValue => {
  const graphGroupCodeList: string[] = Object.values(GRAPH_GROUP_CODE);

  return graphGroupCodeList.includes(value);
};

export const getGraphGroupCodeTypeValue = (value: string): GraphGroupCodeValue | null => {
  if (isGraphGroupCodeType(value)) {
    return value;
  }

  return null;
};

export const isGraphGroupCodeListType = (value: string[]): value is GraphGroupCodeValue[] => {
  const graphGroupCodeList: string[] = Object.values(GRAPH_GROUP_CODE);

  return value.every((item) => graphGroupCodeList.includes(item));
};

export const getGraphGroupCodeListTypeValue = (value: string[]): GraphGroupCodeValue[] | null => {
  if (isGraphGroupCodeListType(value)) {
    return value;
  }

  return null;
};

export const isErrorCodeDisplayDetails = (errorDetails: CellValue | null): errorDetails is ErrorCodeDisplayDetails =>
  errorDetails !== null && typeof errorDetails === 'object' && 'description' in errorDetails;

export const isObjectType = (value: unknown): boolean => {
  return Object.prototype.toString.call(value).slice(8, -1) === 'Object';
};

export const isDisplayedSiteErrorInfoType = (value: unknown): value is DisplayedSiteErrorInfo[] =>
  Array.isArray(value) &&
  value.every((item) => isObjectType(item) && item && 'errorCode' in item && 'triggerDate' in item);

export const isEnergyControlType = (operationMode: string): operationMode is EnergyControlValue => {
  const energyControlValues: string[] = Object.values(ENERGY_CONTROL);

  return energyControlValues.includes(operationMode);
};

export const isGroupSelectOptionListType = (optionList: unknown): optionList is GroupSelectOption[] =>
  Array.isArray(optionList) && optionList.every((option) => isObjectType(option) && option && 'items' in option);

export const isSelectOptionListType = (optionList: unknown): optionList is SelectorOption[] =>
  Array.isArray(optionList) &&
  optionList.every((option) => isObjectType(option) && 'text' in option && 'value' in option);

export const isSiteStatusCodeType = (value: string): value is SiteStatusCodeType =>
  value === '0' || value === '1' || value === '2' || value === '4' || value === '6';

export const isAuthSuccess = (authResponse: Auth.TokenInfoResponse): authResponse is Auth.TokenInfoResponseSuccess =>
  isObjectType(authResponse) && 'access_token' in authResponse;

export const isSettingTargetType = (value: string): value is SettingTargetValue => {
  const settingTargetValues: string[] = Object.values(SETTING_TARGET);

  return settingTargetValues.includes(value);
};

export const isAppModeValueType = (value: string): value is AppModeValue =>
  value === APP_MODE.LOCAL ||
  value === APP_MODE.DEVELOPMENT ||
  value === APP_MODE.STAGING ||
  value === APP_MODE.PRODUCTION;

export const isLocaleValueType = (value: string): value is LanguageValue => {
  const localeList: string[] = Object.values(LANGUAGE);

  return localeList.includes(value);
};

export const isDeviceInfoType = (
  item: MonitoringListSiteInfo | MonitoringListDeviceInfo
): item is MonitoringListDeviceInfo => 'total_device' in item;

export const isESSNumberType = (ESSNumber: number): ESSNumber is ESSNumberType => [1, 2, 3, 4].includes(ESSNumber);

export const getESSNumber = (ESSNumber: number): ESSNumberType | null =>
  isESSNumberType(ESSNumber) ? ESSNumber : null;

export const isUnitValueType = (unit: string): unit is UnitValueType => {
  const unitValueList: string[] = Object.values(UNIT);

  return unitValueList.includes(unit);
};

export const isLanguageValueType = (lang: string): lang is LanguageValue => {
  const languageList: string[] = Object.values(LANGUAGE);

  return languageList.includes(lang);
};

export const getLanguageValue = (lang: string): LanguageValue => (isLanguageValueType(lang) ? lang : LANGUAGE.EN);

export const isSupportedApplianceType = (applianceType: string): applianceType is ApplianceTypeValue => {
  const supportedApplianceList: string[] = Object.values(APPLIANCE_TYPE);

  return supportedApplianceList.includes(applianceType);
};

export const isNonNullableType = <T>(value: T | null | undefined): value is T => !isNil(value);

export const isGridCheckEventType = (event: unknown): event is Row => {
  return typeof event === 'object' && event !== null && 'rowKey' in event;
};

export const isGridEventType = (event: unknown): event is GridEventType => {
  return typeof event === 'object' && event !== null;
};

export const isHTMLElementType = (value: EventTarget | null): value is HTMLElement => {
  return value instanceof HTMLElement;
};

export const isSiteConfigurationType = (value: RouteValueType): value is SiteConfigurationType =>
  value === SITE_CONFIGURATION_TYPE.SOLAR ||
  value === SITE_CONFIGURATION_TYPE.BACKUP ||
  value === SITE_CONFIGURATION_TYPE.GRID;

export const isFirmwareUpdateVersionType = (key: string): key is FirmwareFileVersionKey => {
  return ['emsVersion', 'pcsVersion', 'gemVersion', 'hubVersion', 'bmsVersion'].includes(key);
};

export const isFirmwareUpdateFileType = (key: string): key is FirmwareFileNameKey => {
  return ['emsFileName', 'pcsFileName', 'gemFileName', 'hubFileName', 'bmsFileName'].includes(key);
};

export const isFirmwareUpdateEMSFileType = (key: string): key is FirmwareEMSFileNameKey => {
  return ['appFileName', 'fullFileName'].includes(key);
};

export const isFirmwareUpdateType = (value: RouteValueType): value is FirmwareUpdateType =>
  value === FIRMWARE_UPDATE_TYPE.AUTO || value === FIRMWARE_UPDATE_TYPE.MANUAL;

export const isFirmwareUpdateStatusType = (value: string): value is FirmwareUpdateStatus =>
  value === FIRMWARE_UPDATE_STATUS.NONE ||
  value === FIRMWARE_UPDATE_STATUS.DOWNLOAD_STARTED ||
  value === FIRMWARE_UPDATE_STATUS.UPDATE_STARTED;

export const isErrorLevelType = (value: string | null): value is ErrorLevelValue => {
  return value !== null && Object.values(ERROR_LEVEL).includes(value as ErrorLevelValue);
};

export const isEMS09GridCodeProfileName = (name: string): name is Over09EMSVersionGridCode => {
  return name === OVER_09_EMS_VERSION_GRID_CODE.LUMA || name === OVER_09_EMS_VERSION_GRID_CODE.IEEE_1547_2018_EXTEND;
};

export const isActivityHistoryAvailableSortColumnType = (value: string): value is ActivityHistorySortColumn => {
  const availableSortColumnList: string[] = Object.values(ACTIVITY_HISTORY_SORT_COLUMN);

  return availableSortColumnList.includes(value);
};

export const isEnergyPolicyType = (value: number): value is EnergyPolicyValue => {
  const energyPolicyValues: number[] = Object.values(ENERGY_POLICY);

  return energyPolicyValues.includes(value);
};

export const isFleetRoleType = (value: string): value is FleetRoleValue => {
  const fleetRoleValues: string[] = Object.values(FLEET_ROLE);

  return fleetRoleValues.includes(value);
};

export const isDeviceSettingTargetType = (value: string): value is ACSystemFieldTypeCodeValue => {
  const settingTargetValues: string[] = Object.values(AC_SYSTEM_FIELD_TYPE_CODE);

  return settingTargetValues.includes(value);
};

export const isPhysicalUnitType = (unit: UnitValueType): unit is BasePhysicalUnitType =>
  unit !== UNIT.PERCENT &&
  unit !== UNIT.CELSIUS &&
  unit !== UNIT.FACTOR &&
  unit !== UNIT.DEGREE &&
  unit !== UNIT.FAHRENHEIT;
